import { Entitlement, Subscription } from "~/shared/enums";
import type { TenantEntitlements } from "~/shared/types";

export const DEFAULT_TRIAL_TYPE = Subscription.PREMIUM;
export const ORDERED_SUBSCRIPTIONS = Object.values(Subscription);
export const SUBSCRIPTION_TO_INDEX = Object.fromEntries(ORDERED_SUBSCRIPTIONS.map((e, i) => [e, i]));

export const APP_SUMO_MAX_USERS = 2;
export const APP_SUMO_TIER_TO_MAX_USERS: Record<number, number> = {
  1: APP_SUMO_MAX_USERS,
  2: 5,
  3: 10,
  4: 20,
  5: 40,
};

export const HIDDEN_SUBSCRIPTIONS = new Set([Subscription.APP_SUMO, Subscription.ENTERPRISE]);

export const DEFAULT_ENTITLEMENTS: Record<Subscription, Partial<TenantEntitlements>> = {
  [Subscription.PERSONAL]: {
    [Entitlement.MAX_USERS]: 4,
    [Entitlement.ROLES]: false,
    [Entitlement.STATUSES]: false,
    [Entitlement.MAX_CUSTOM_PROPERTIES]: 1,
    [Entitlement.MAX_DASHBOARDS]: 1,
    [Entitlement.MAX_FORMS]: 1,
    [Entitlement.MAX_PUBLIC_VIEWS]: 1,
    [Entitlement.MAX_TASK_KINDS]: 5,
    [Entitlement.DISCORD]: false,
    [Entitlement.GITHUB]: false,
    [Entitlement.SLACK]: false,
    [Entitlement.WEBHOOKS]: false,
    [Entitlement.ZAPIER]: false,
  },
  [Subscription.APP_SUMO]: {
    [Entitlement.MAX_USERS]: APP_SUMO_MAX_USERS,
    [Entitlement.ROLES]: true,
    [Entitlement.STATUSES]: true,
    [Entitlement.MAX_CUSTOM_PROPERTIES]: Infinity,
    [Entitlement.MAX_FORMS]: Infinity,
    [Entitlement.MAX_TASK_KINDS]: Infinity,
    [Entitlement.DISCORD]: true,
    [Entitlement.GITHUB]: true,
    [Entitlement.SLACK]: true,
  },
  [Subscription.PREMIUM]: {
    [Entitlement.MAX_USERS]: Infinity,
    [Entitlement.WEBHOOKS]: true,
    [Entitlement.ZAPIER]: true,
  },
  [Subscription.BUSINESS]: {
    [Entitlement.MAX_DASHBOARDS]: Infinity,
    [Entitlement.MAX_PUBLIC_VIEWS]: Infinity,
  },
  [Subscription.ENTERPRISE]: {},
};

export const BILLING_FEATURES = [
  {
    title: "Teammates",
    [Subscription.PERSONAL]: "Up to 4",
    [Subscription.APP_SUMO]: "Unlimited",
  },
  {
    title: "Tasks",
    [Subscription.PERSONAL]: "Unlimited",
  },
  {
    title: "Docs",
    [Subscription.PERSONAL]: "Unlimited",
  },
  {
    title: "Storage",
    [Subscription.PERSONAL]: "Unlimited",
  },
  {
    title: "AI model usage",
    [Subscription.PERSONAL]: "Unlimited",
  },
  {
    title: "AI agents",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "AI task execution",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "AI reporting",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "AI task property filling",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "AI subtask generation",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "AI slash commands",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "Duplicate task detection",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "ChatGPT integration",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "Claude integration",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "Model Context Protocol (MCP) server for any AI or LLM integration",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "Command center and keyboard shortcuts",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "Voice controls",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "Mac, Windows, and Linux desktop apps",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "iOS and Android mobile apps",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "Dark mode and light mode themes",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "Natural language property filling",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "Real-time collaboration, comments, and notifications",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "Migration",
    [Subscription.PERSONAL]: "Automated",
    [Subscription.BUSINESS]: "Dedicated support",
  },
  {
    title: "List, board, roadmap, and calendar layouts",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "Filters, sorting, views, and search",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "Subtasks, blockers, dependencies, and other relationships",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "Task types for epics, issues, stories, projects, bugs, milestones, clients, and more",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "Sprint management",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "Support",
    [Subscription.PERSONAL]: "✓",
    [Subscription.PREMIUM]: "One day SLA",
    [Subscription.ENTERPRISE]: "Dedicated manager",
  },
  {
    title: "Import and export",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "Notion integration",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "Email integration",
    [Subscription.PERSONAL]: "✓",
  },
  {
    title: "AI chat",
    [Subscription.PERSONAL]: "",
    [Subscription.APP_SUMO]: "✓",
  },
  {
    title: "AI skills",
    [Subscription.PERSONAL]: "",
    [Subscription.APP_SUMO]: "✓",
  },
  {
    title: "AI roadmap planning",
    [Subscription.PERSONAL]: "",
    [Subscription.APP_SUMO]: "✓",
  },
  {
    title: "AI brainstorming",
    [Subscription.PERSONAL]: "",
    [Subscription.APP_SUMO]: "✓",
  },
  {
    title: "Admin and guest roles",
    [Subscription.PERSONAL]: "",
    [Subscription.APP_SUMO]: "✓",
  },
  {
    title: "Custom statuses and properties",
    [Subscription.PERSONAL]: "",
    [Subscription.APP_SUMO]: "✓",
  },
  {
    title: "Forms",
    [Subscription.PERSONAL]: "",
    [Subscription.APP_SUMO]: "✓",
  },
  {
    title: "API, Python, TypeScript, JavaScript, and command line libraries",
    [Subscription.PERSONAL]: "",
    [Subscription.PREMIUM]: "✓",
  },
  {
    title: "Webhooks",
    [Subscription.PERSONAL]: "",
    [Subscription.PREMIUM]: "✓",
  },
  {
    title: "n8n integration",
    [Subscription.PERSONAL]: "",
    [Subscription.PREMIUM]: "✓",
  },
  {
    title: "Slack integration and automations",
    [Subscription.PERSONAL]: "",
    [Subscription.APP_SUMO]: "✓",
  },
  {
    title: "Discord integration",
    [Subscription.PERSONAL]: "",
    [Subscription.APP_SUMO]: "✓",
  },
  {
    title: "GitHub integration and automations",
    [Subscription.PERSONAL]: "",
    [Subscription.APP_SUMO]: "✓",
  },
  {
    title: "Zapier integration",
    [Subscription.PERSONAL]: "",
    [Subscription.PREMIUM]: "✓",
  },
  {
    title: "ZohoFlow integration",
    [Subscription.PERSONAL]: "",
    [Subscription.PREMIUM]: "✓",
  },
  {
    title: "Pipedream integration",
    [Subscription.PERSONAL]: "",
    [Subscription.PREMIUM]: "✓",
  },
  {
    title: "SAML SSO",
    [Subscription.PERSONAL]: "",
    [Subscription.BUSINESS]: "✓",
  },
  {
    title: "SCIM provisioning",
    [Subscription.PERSONAL]: "",
    [Subscription.PREMIUM]: "",
    [Subscription.BUSINESS]: "✓",
  },
  {
    title: "Public views",
    [Subscription.PERSONAL]: "",
    [Subscription.BUSINESS]: "✓",
  },
  {
    title: "Advanced analytics",
    [Subscription.PERSONAL]: "",
    [Subscription.BUSINESS]: "✓",
  },
  {
    title: "Customizable dashboards",
    [Subscription.PERSONAL]: "",
    [Subscription.BUSINESS]: "✓",
  },
  {
    title: "Granular access management",
    [Subscription.PERSONAL]: "",
    [Subscription.BUSINESS]: "✓",
  },
  {
    title: "Release channel access",
    [Subscription.PERSONAL]: "Standard",
    [Subscription.PREMIUM]: "Beta",
    [Subscription.ENTERPRISE]: "Alpha",
  },
].map((e, i) => ({
  ...e,
  id: i,
}));
